/**
 * Fiscal period helpers for the CRO scorecard tiles.
 *
 * Adeptia's fiscal year runs Jan–Dec; quarters are calendar quarters (Q1=Jan-Mar, etc.).
 * The target period label (e.g., "FY26 Q2") is derived from `target_period` on the
 * CroMetricResponse shape (Wave 5 Plan 07 API).
 */

/**
 * Format a fiscal period object into a compact label string.
 * Examples:
 *   { fiscal_year: 2026, fiscal_quarter: 2 } → "FY26 Q2"
 *   { fiscal_year: 2026, fiscal_quarter: null } → "FY26"
 *   null → "" (no label)
 */
export function formatFiscalPeriod(period: {
  fiscal_year: number | null;
  fiscal_quarter: number | null;
} | null): string {
  if (!period) return '';
  const { fiscal_year, fiscal_quarter } = period;
  if (!fiscal_year) return '';
  const yearShort = String(fiscal_year).slice(-2);
  if (fiscal_quarter) {
    return `FY${yearShort} Q${String(fiscal_quarter)}`;
  }
  return `FY${yearShort}`;
}

/**
 * Format a percent_to_target ratio (0.0–N.NN) as a display label.
 * e.g., 0.95 → "95%", 1.12 → "112%"
 */
export function formatPercentToTarget(ratio: number): string {
  return `${(ratio * 100).toFixed(0)}%`;
}

/**
 * Determine the chip color class based on percent-to-target ratio.
 * Used by both CroTile and CroTrendTile.
 *
 * Returns a tuple [bgClass, textClass] for Tailwind utility application.
 */
export function targetChipVariant(ratio: number): 'good' | 'warn' | 'bad' {
  if (ratio >= 1.0) return 'good';
  if (ratio >= 0.8) return 'warn';
  return 'bad';
}
